<template>
  <div class="ai-assistant-page">
    <!-- ===== Global Loader ===== -->
    <!-- <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{loadingText}}</p>
      </div>
    </transition> -->

    <!-- ===== Header ===== -->
    <div class="assistant-header">
      <div class="assistant-title">
        <div class="assistant-avatar">
          <i class='bx bx-bot' style="font-size: 1.5rem;"></i>
        </div>
        <h2>AI Assistant</h2>
      </div>

      <div class="assistant-select">
        <select v-model="selectedWebsite" @change="initConversation">
          <option value="">Global Analytics (All Data)</option>
          <option v-for="web in websites" :key="web.website_id" :value="web.website_id">
            {{ web.website_domain }}
          </option>
        </select>
      </div>
    </div>

    <!-- ===== Chat Body ===== -->
    <div class="chat-body" ref="chatContainer">
      <!-- Placeholder if no website selected -->
      <div
        v-if="!selectedWebsite && !loading"
        class="flex flex-col items-center justify-center text-gray-500 mt-24"
      >
        <i class="bx bx-bot text-4xl mb-3 text-blue-500"></i>
        <p class="text-sm">Select a website to start chatting with your AI assistant.</p>
      </div>

      <!-- Empty conversation -->
      <div
        v-else-if="messages.length === 0 && !loading"
        class="flex flex-col items-center justify-center text-gray-400 mt-24 italic"
      >
        <i class="bx bx-message-dots text-4xl mb-3 text-blue-400"></i>
        <p class="text-sm">Ask me anything from websites to invoices, I’ve got you.</p>
      </div>

      <!-- Chat messages -->
      <transition-group name="chat" tag="div" class="chat-scroll">
        <div
          v-for="(msg, i) in messages"
          :key="i"
          :class="['chat-bubble', msg.sender === 'me' ? 'me' : 'ai']"
          
        >
          <div class="chat-text"  v-html="msg.text"></div>
          <div class="chat-time">{{ msg.time }}</div>
        </div>

        <!-- AI Typing Indicator -->
        <div v-if="aiTyping" key="typing" class="chat-bubble ai typing-bubble">
          <div class="typing-dots">
            <span></span><span></span><span></span>
          </div>
          <div class="chat-time">Typing...</div>
        </div>
      </transition-group>
    </div>

    <!-- ===== Chat Footer ===== -->
    <div class="chat-footer">
     
      <input
        type="text"
        v-model="messageInput"
        placeholder="Type your message..."
        @keyup.enter="sendMessageNOw"
        :disabled="loading || aiTyping"
      />

      <i
        class="bx bx-microphone text-gray-500 hover:text-blue-600 cursor-pointer"
        style="font-size:22px;"
        @click="startSpeechRecognition"
      ></i>

      <!-- SEND BUTTON (unchanged) -->
      <button class="send-btn" @click="sendMessageNOw" :disabled="loading || aiTyping || !selectedWebsite">
        <i v-if="!aiTyping" class="bx bx-send"></i>
        <i v-else class="bx bx-loader-alt animate-spin"></i>
      </button>
    </div>

    <!-- ===== Toast Area ===== -->
    <transition name="fade">
      <div
        v-if="toastMessage"
        class="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 border border-blue-200 px-4 py-2 rounded-lg shadow-sm text-sm font-medium z-50"
      >
        {{ toastMessage }}
      </div>
    </transition>


  </div>
  
<!-- 🎤 Recording Popup -->
<div
  v-if="isRecPopup"
  class="voice-popup-overlay"
>
  <div class="voice-popup-box">
    
    <!-- Close -->
    <i
      class="bx bx-x voice-popup-close"
      @click="stopSpeechPopup"
    ></i>

    <div
  :class="[
    'voice-mic-circle',
    isRecording ? 'voice-mic-recording' : 'voice-mic-idle'
  ]"
>
  <i
    :class="[
      'bx bx-microphone voice-mic-icon',
      isRecording ? 'text-red-600' : 'text-blue-600'
    ]"
  ></i>
</div>


    <p class="voice-popup-text">
      {{ isRecording ? "Listening..." : "Preparing microphone..." }}
    </p>

  </div>
</div>

</template>

<script>
import { useToast } from "vue-toastification";
import {
  getLastConversation,
  createConversation,
  getMessages,
  sendMessage,
  getAIMessage,
} from "@/apis/chatapis";
import { getWebsites } from "@/apis/webapis";
import { getAnaReport } from "@/apis/adminapis";

export default {
  name: "AiAssistant",
  data() {
    return {
      isRecPopup: false,
      isRecording: false,
      recognition: null,
      toast: useToast(),
      userId: "",
      selectedWebsite: "",
      websites: [],
      messageInput: "",
      aiTyping: false,
      anadata:{},
      messages: [],
      loading: false,
      loadingText: "Loading assistant...",
      convId: null,
    };
  },

  async mounted() {
    const ldataRaw = sessionStorage.getItem("ldata");
    const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;

    if (!ldata) {
      this.toast.error("Session expired. Please log in again.");
      window.location.href = "/login";
      return;
    }

    this.userId = ldata.user_id;
    await this.loadWebsites();
  },

  methods: {
    startSpeechRecognition() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        this.toast.error("Your browser does not support speech recognition.");
        return;
      }

      // Create recognizer
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = "en-US";

      /** IMPORTANT FIX:
       * If popup opens BEFORE recognition.start(),
       * Chrome ABORTS recording. So we delay!
       */

      // Start recognition FIRST
      try {
        this.recognition.start();
      } catch (err) {
        // Chrome throws error if start() is called twice
        console.log("Recognition already running…");
      }

      // Open popup AFTER a short delay
      setTimeout(() => {
        this.isRecPopup = true;
        this.isRecording = true;
      }, 150);

      this.recognition.onstart = () => {
        console.log("Speech recognition started");
      };

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;

        this.isRecording = false;
        this.isRecPopup = false;

        // Auto-fill input + auto-send
        this.messageInput = transcript;
        this.sendMessageNOw();
      };

      this.recognition.onerror = (event) => {
        console.log("Speech recognition error:", event.error);

        // Ignore abort error caused by silence
        if (event.error === "aborted" || event.error === "no-speech") {
          this.toast.warning("Didn't catch that, try again.");
        } else {
          this.toast.error("Microphone error.");
        }

        this.isRecording = false;
        this.isRecPopup = false;
      };

      this.recognition.onend = () => {
        // If ended unexpectedly
        if (this.isRecording) {
          this.isRecording = false;
          this.isRecPopup = false;
        }
      };
    }
    , 

    stopSpeechPopup() {
      if (this.recognition) {
        try {
          this.recognition.stop();
        } catch (err) {
          console.log("Stop error:", err);
        }
      }

      this.isRecording = false;
      this.isRecPopup = false;
    }
    ,

    async fetchAnalytics() {
       

        try {
          const res = await getAnaReport(this.selectedWebsite, "", "", "");

          if (res.status && res.report_dt) {
            this.anadata = res.report_dt;
          }
        } catch (error) {
          console.error("fetchAnalytics error:", error);
        }
      },
    /** 🧠 Load websites */
    async loadWebsites() {
      try {
        this.loading = true;
        this.loadingText = "Loading your websites...";
        

        const res = await getWebsites(this.userId, "none", "", "");
        if (res?.status && Array.isArray(res.records)) {
          this.websites = res.records;
         await this.initConversation();
        } else {
          this.toast.warning("No websites found for this account.");
        }
      } catch (err) {
        console.error("Website Load Error:", err);
        this.toast.error("Failed to load websites.");
      } finally {
        this.loading = false;
      }
    },

    /** 💬 Initialize or fetch conversation */
    async initConversation() {
      await this.fetchAnalytics();
      try {
        this.loading = true;
        this.loadingText = "Loading conversation...";
        const res = await getLastConversation(this.userId, this.selectedWebsite);
        if (res?.status && res.conv_dt) {
          this.convId = res.conv_dt.conv_id;
          await this.loadMessages();
        } else {
          const created = await createConversation(this.userId, this.selectedWebsite);
          if (created?.status && created.conv_dt) {
            this.convId = created.conv_dt.conv_id;
          }
        }
      } catch (err) {
        console.error("Init Conversation Error:", err);
        this.toast.error("Failed to initialize chat.");
      } finally {
        this.loading = false;
      }
    },

    /** 📨 Load messages for conversation */
    async loadMessages() {
      if (!this.convId) return;
      try {
        this.loading = true;
        this.loadingText = "Fetching messages...";

        const res = await getMessages(this.convId, this.userId, "none");
        if (res?.status && Array.isArray(res.records)) {
          this.messages = res.records.map((m) => ({
            sender: m.msg_user1 !== "Bot" ? "ai" : "me",
            text: this.formatAiResponse(m.msg_txt),
            time: new Date(m.msg_datetime).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          }));
        } else {
          this.messages = [];
        }
      } catch (err) {
        console.error("Load Messages Error:", err);
        this.toast.error("Unable to load conversation messages.");
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      }
    },

    /** ✉️ Send message */
    async sendMessageNOw() {
      if (!this.messageInput.trim() || !this.convId) return;

      // Append user message instantly
      const newMsg = {
        sender: "me",
        text: this.messageInput,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
      this.messages.push(newMsg);
      const msgTxt = this.messageInput;
      this.messageInput = "";
      this.scrollToBottom();

      try {
        this.aiTyping = true;
        const sent = await sendMessage(msgTxt, this.convId, this.userId);

        if (sent?.status && sent.msg_dt) {
          const aiRes = await getAIMessage(sent.msg_dt.msg_id, this.anadata);
          if (aiRes?.status) {
            await this.loadMessages();
          } else {
            this.toast.warning("AI response unavailable.");
          }
        }
      } catch (err) {
        console.error("Send Message Error:", err);
        this.toast.error("Failed to send message.");
      } finally {
        this.aiTyping = false;
        this.scrollToBottom();
      }
    },

    /** 🧾 Format AI text to be readable */
    formatAiResponse(raw) {
      if (!raw) return raw;
      let formatted = raw
        .replace(/###\s?(.*?)(\n|$)/g, "<h5 style='font-weight:600;margin-top:0.75rem;'>$1</h5>")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/Summary:/gi, "<h4 style='font-weight:600;margin-top:0.5rem;'>📊 Summary</h4>")
        .replace(/Insights:/gi, "<h4 style='font-weight:600;margin-top:0.75rem;'>💡 Insights</h4>")
        .replace(/Recommendations:/gi, "<h4 style='font-weight:600;margin-top:0.75rem;'>🧭 Recommendations</h4>")
        .replace(/(\d+)\.\s/g, "<br><span style='margin-left:0.75rem;'>$1.</span> ")
        .replace(/-\s/g, "<br>• ")
        .replace(/\n+/g, "<br>");
      return `<div>${formatted}</div>`;
    },

    /** 🔽 Scroll to latest message */
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },
  },
};
</script>


<style scoped>

/* === Layout === */
.ai-assistant-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f9fafb;
  border-radius: 16px;
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.05);
  overflow: hidden;
  position: relative;
}

/* === Header === */
.assistant-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 18px 24px;
}
.assistant-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.assistant-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #111827, #1e3a8a);
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.assistant-select {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  color: #374151;
}

.assistant-select select {
  font-size: 14px;
  color: #374151;
  border: 1px solid #fff;
  border-radius: 8px;
}

/* === Chat Body === */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 90px;
  background: #f3f4f6;
}
.chat-scroll {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.chat-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.45;
  box-shadow: 0 2px 10px rgba(17, 24, 39, 0.08);
  word-wrap: break-word;
  animation: fadeInUp 0.3s ease;
}
.chat-bubble.me {
  align-self: flex-end;
  background: linear-gradient(135deg, #111827, #1e3a8a);
  color: #fff!important;
  border-bottom-right-radius: 6px;
}
.chat-bubble.ai {
  align-self: flex-start;
  background: #fff;
  color: #111827!important;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 6px;
}

/* === Fixed Footer === */
.chat-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  z-index: 100;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}
.chat-footer input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  background: #f9fafb;
}
.chat-footer input:focus {
  border-color: #1e3a8a;
  box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
}

.send-btn {
  background: #1e3a8a;
  color: #fff;
  border: none;
  padding: 10px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.send-btn:hover {
  background: #111827;
  transform: translateY(-1px);
}

/* === Time + Typing === */
.chat-time {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  text-align: right;
}
.typing-dots {
  display: flex;
  align-items: center;
  gap: 4px;
}
.typing-dots span {
  width: 6px;
  height: 6px;
  background: #1e3a8a;
  border-radius: 50%;
  animation: bounce 1.2s infinite ease-in-out;
}
.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  40% {
    transform: scale(1.4);
    opacity: 1;
  }
}

/* === Scrollbar === */
.chat-body::-webkit-scrollbar {
  width: 6px;
}
.chat-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 6px;
}

/* === Responsive === */
@media (max-width: 768px) {
  .assistant-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
    padding: 12px 16px;
  }

  .chat-body {
    padding-bottom: 100px;
  }

  .chat-footer {
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 10px 12px;
    gap: 8px;
  }

  .chat-footer input {
    flex: 1 1 100%;
    width: 100%;
  }

  .send-btn {
    width: 100%;
    padding: 10px;
    justify-content: center;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .assistant-header {
    padding: 10px 12px;
  }

  .assistant-avatar {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .assistant-title h2 {
    font-size: 16px;
  }

  .chat-bubble {
    max-width: 90%;
    font-size: 13px;
  }

  .chat-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .send-btn {
    width: 100%;
  }
}

/* === FIXED FOOTER INSIDE CHAT BOX === */
.ai-assistant-page {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 80vh;
  overflow: hidden;
}

/* Body scrolls, footer stays visible inside the box */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 80px; /* padding bottom for footer space */
  background: #f3f4f6;
}

/* Footer now sticks to the bottom INSIDE the parent box */
.chat-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

/* === Mobile adjustments === */
@media (max-width: 768px) {
  .chat-body {
    padding-bottom: 100px;
  }

  .chat-footer {
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 10px 12px;
    gap: 8px;
  }

  .chat-footer input {
    flex: 1 1 100%;
    width: 100%;
  }

  .send-btn {
    width: 100%;
    justify-content: center;
  }
}
/* === Loader Overlay === */
.absolute {
  position: absolute;
}
.inset-0 {
  inset: 0;
}
.backdrop-blur-sm {
  backdrop-filter: blur(6px);
}
.animate-spin {
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* === Toast Notification === */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* === AI Typing Dots === */
.typing-bubble {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.typing-dots {
  display: flex;
  align-items: center;
  gap: 4px;
}
.typing-dots span {
  width: 6px;
  height: 6px;
  background: #1e3a8a;
  border-radius: 50%;
  animation: bounce 1.2s infinite ease-in-out;
}
.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.6; }
  40% { transform: scale(1.4); opacity: 1; }
}

/* === Toast Position === */
.absolute.top-4 {
  top: 1rem;
}
.left-1\/2 {
  left: 50%;
}
.-translate-x-1\/2 {
  transform: translateX(-50%);
}

/* === Chat Animations === */
.chat-enter-active,
.chat-leave-active {
  transition: all 0.25s ease-out;
}
.chat-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.chat-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* === AI Loader Icon Fix === */
.bx-loader-alt.animate-spin {
  animation: spin 0.9s linear infinite;
}
/* === MICROPHONE POPUP OVERLAY === */
.voice-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease-out;
}

/* === POPUP BOX === */
.voice-popup-box {
  background: #ffffff;
  width: 280px;
  padding: 24px 20px;
  border-radius: 18px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  position: relative;
  text-align: center;
  animation: slideUp 0.25s ease-out;
}

/* === CLOSE BUTTON === */
.voice-popup-close {
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 22px;
  color: #6b7280;
  cursor: pointer;
  transition: 0.2s ease;
}
.voice-popup-close:hover {
  color: #111827;
  transform: scale(1.1);
}

/* === MIC CIRCLE === */
.voice-mic-circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  margin: 0 auto 16px auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Idle mic */
/* Idle state (optional slightly lighter blue) */
.voice-mic-idle {
  background: #1f2937; /* lighter dark blue */
}

/* Recording state — DARK BLUE */
.voice-mic-recording {
  background: #1f2937 !important;
  animation: pulseMic 1.2s infinite ease-in-out;
}

/* Mic Icon inside — White */
.voice-mic-icon {
  font-size: 42px;
  color: #ffffff !important;
}

/* MIC ICON INSIDE */
.voice-mic-icon {
  font-size: 42px;
}

/* === TEXT === */
.voice-popup-text {
  font-size: 14px;
  color: #6b7280;
}

/* === Animations === */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes pulseMic {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5);
  }
  50% {
    transform: scale(1.07);
    box-shadow: 0 0 10px 4px rgba(239, 68, 68, 0.25);
  }
}

</style>
